import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  type RentalManagerUserPropertyMultiplexWebApiHandler_upload_Args,
  type RentalManagerUserPropertyMultiplexWebApiHandler_upload_Options,
  useRentalManagerUserPropertyMultiplexWebApiHandler_upload,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import type { Client } from '@zg-rentals/http-client';
import type { UseMutationOptions } from '@zg-rentals/react-query5';
import type { ApiResponseErrorDto } from '@zg-rentals/ts-rental-manager-api-client';

/**
 * Same shape as `UserPropertyMultiplexPhotoUploadRequestWebDto` from the generated API client, sourced from the
 * multiplex handler `upload` mutation args.
 */
export type UserPropertyMultiplexPhotoUploadRequestWebDto =
  RentalManagerUserPropertyMultiplexWebApiHandler_upload_Args['postData'];

/**
 * Same shape as `UserPropertyMultiplexPhotoUploadResponseWebDto` from the generated API client, sourced from the
 * multiplex handler `upload` mutation result type.
 */
export type UserPropertyMultiplexPhotoUploadResponseWebDto =
  RentalManagerUserPropertyMultiplexWebApiHandler_upload_Options extends UseMutationOptions<
    infer Data,
    unknown,
    RentalManagerUserPropertyMultiplexWebApiHandler_upload_Args
  >
    ? Data
    : never;

const toApiResponseErrorDto = (error: unknown): ApiResponseErrorDto<unknown> | undefined => {
  if (!DatarouterError.is(error)) {
    return undefined;
  }

  return {
    code: error.code ?? '',
    message: error.message,
    data: error.errorDetails,
  };
};

/**
 * Runtime guard beyond compile-time args:
 * `getParams.multiplexBuildingSessionId` must be a non-empty string; `postData.sourceFile` must be a {@link Blob}.
 */
const assertMultiplexPhotoUploadMutationArgs = (
  args: RentalManagerUserPropertyMultiplexWebApiHandler_upload_Args,
): void => {
  const { postData, getParams } = args;

  if (typeof postData !== 'object' || postData === null || Array.isArray(postData)) {
    throw new TypeError(
      'useMultiplexPhotoUpload: `postData` must be a non-array object (UserPropertyMultiplexPhotoUploadRequestWebDto).',
    );
  }

  if (!(postData.sourceFile instanceof Blob)) {
    throw new TypeError('useMultiplexPhotoUpload: `postData.sourceFile` must be a Blob.');
  }

  if (typeof getParams !== 'object' || getParams === null) {
    throw new TypeError('useMultiplexPhotoUpload: `getParams` is required (UploadParams).');
  }

  const sessionId = getParams.multiplexBuildingSessionId;

  if (typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new TypeError(
      'useMultiplexPhotoUpload: `getParams.multiplexBuildingSessionId` is required on every call (non-empty string).',
    );
  }
};

export type UseMultiplexPhotoUploadOptions = Omit<
  RentalManagerUserPropertyMultiplexWebApiHandler_upload_Options,
  'mutationFn'
> & {
  httpClient?: Client;
};

type BaseMultiplexPhotoUploadMutation = ReturnType<typeof useRentalManagerUserPropertyMultiplexWebApiHandler_upload>;

export type UseMultiplexPhotoUploadResult = Omit<
  BaseMultiplexPhotoUploadMutation,
  'mutate' | 'mutateAsync' | 'data' | 'error' | 'isPending'
> & {
  mutate: (
    args: RentalManagerUserPropertyMultiplexWebApiHandler_upload_Args,
    options?: Parameters<BaseMultiplexPhotoUploadMutation['mutate']>[1],
  ) => ReturnType<BaseMultiplexPhotoUploadMutation['mutate']>;
  mutateAsync: (
    args: RentalManagerUserPropertyMultiplexWebApiHandler_upload_Args,
    options?: Parameters<BaseMultiplexPhotoUploadMutation['mutateAsync']>[1],
  ) => ReturnType<BaseMultiplexPhotoUploadMutation['mutateAsync']>;
  data: BaseMultiplexPhotoUploadMutation['data'];
  error: BaseMultiplexPhotoUploadMutation['error'];
  isPending: BaseMultiplexPhotoUploadMutation['isPending'];
  errors: ApiResponseErrorDto<unknown> | undefined;
  success: boolean | undefined;
  errorMessage: UserPropertyMultiplexPhotoUploadResponseWebDto['errorMessage'] | undefined;
  units: UserPropertyMultiplexPhotoUploadResponseWebDto['units'] | undefined;
};

/**
 * Multiplex photo upload: wraps the generated `upload` mutation (POST `/api/web/v1/users/properties/multiplex/photos/upload`).
 *
 * Merges default `meta`: invalidates tag `multiplexBuildingSessionDetails` (plus any `meta.invalidates` you pass) and
 * sets `meta.context` for logging unless overridden.
 *
 * Exposes the usual mutation API (`mutate`, `mutateAsync`, `reset`, `data`, `error`, `isPending`, ...). Also returns
 * `errors` (normalized shape when the request throws), and `success`, `errorMessage`, and `units` from the last
 * successful `data`.
 *
 * **Calling the mutation:** Use `{ getParams: { multiplexBuildingSessionId }, postData }` where `postData` matches
 * {@link UserPropertyMultiplexPhotoUploadRequestWebDto} (`sourceFile` as {@link Blob}). Compile-time typing applies to
 * `mutate` / `mutateAsync`; runtime checks enforce `postData`, `getParams`, and a non-empty session id string.
 */
const useMultiplexPhotoUpload = (
  mutationOptions: UseMultiplexPhotoUploadOptions = {},
): UseMultiplexPhotoUploadResult => {
  const { meta: metaOverrides, ...restMutationOptions } = mutationOptions;

  const mutationResult = useRentalManagerUserPropertyMultiplexWebApiHandler_upload({
    ...restMutationOptions,
    meta: {
      ...metaOverrides,
      invalidates: ['multiplexBuildingSessionDetails', ...(metaOverrides?.invalidates ?? [])],
      context: {
        api: 'useRentalManagerUserPropertyMultiplexWebApiHandler_upload',
        details: 'useMultiplexPhotoUpload',
        ...metaOverrides?.context,
      },
    },
  });

  const { data, error, isPending, mutate, mutateAsync, ...rest } = mutationResult;

  return {
    ...rest,
    mutate: (args, options) => {
      assertMultiplexPhotoUploadMutationArgs(args);
      return mutate(args, options);
    },
    mutateAsync: (args, options) => {
      assertMultiplexPhotoUploadMutationArgs(args);
      return mutateAsync(args, options);
    },
    data,
    error,
    isPending,
    errors: toApiResponseErrorDto(error),
    success: data?.success,
    errorMessage: data?.errorMessage,
    units: data?.units,
  };
};

export default useMultiplexPhotoUpload;
